import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import { testimonials } from "@/mocks/comics";

const principles = [
  {
    icon: "ri-refresh-line",
    title: "稳定更新优先",
    desc: "所有入选作品均承诺持续稳定更新，以扎实的更新节奏回馈每一位追更读者。",
  },
  {
    icon: "ri-archive-drawer-line",
    title: "精选长篇精品",
    desc: "宁缺毋滥，我们只收录口碑与质量双高的长篇连载，避免烂尾与敷衍之作。",
  },
  {
    icon: "ri-eye-off-line",
    title: "阅读体验清爽",
    desc: "零广告打扰、排版清晰、加载流畅，把纯粹舒服的阅读体验留给读者。",
  },
  {
    icon: "ri-heart-3-line",
    title: "尊重创作与读者",
    desc: "尊重每一位创作者的劳动，也珍视每一位读者的陪伴，用心运营每一部连载。",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 - 天天漫画网"
        description="了解天天漫画网：一家以长期连载、稳定更新为核心的漫画平台，坚持精选长篇精品、清爽阅读体验，做百万读者的追更神器。"
        keywords="天天漫画网,关于我们,长期连载,追更神器,漫画平台"
        canonical={`${SITE_URL}/about`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于天天漫画网",
            url: `${SITE_URL}/about`,
            description: "了解天天漫画网的品牌介绍、发展理念、内容原则与口碑。",
          },
        ]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">关于我们</span>
        </nav>

        <header className="mt-5 max-w-3xl">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">关于天天漫画网</h1>
          <p className="mt-3 leading-relaxed text-foreground-700">
            天天漫画网是一个以「长期连载 · 稳定更新」为核心的漫画阅读平台。我们相信，一部优秀的长篇漫画值得被完整地追完，
            而追更的快乐不该被断更、停滞或杂乱无章的更新所打断。因此，我们专注做一件事——把最值得追的长篇连载集中呈现，并确保它们稳定更新、持续陪伴。
          </p>
        </header>

        <section className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-focus-3-line text-xl"></i>
            </span>
            <h2 className="mt-4 text-lg font-bold text-foreground-950">网站定位</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-700">
              做国内口碑优秀的长期连载漫画库与追更工具。我们服务热爱长篇故事、追求持续更新体验的漫画读者，
              用稳定可靠的更新节奏，成为读者心中的「追更神器」。
            </p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-compass-3-line text-xl"></i>
            </span>
            <h2 className="mt-4 text-lg font-bold text-foreground-950">发展理念</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-700">
              我们始终相信「陪伴」的价值。一部连载漫画与读者之间的信任，建立在一次次准时到来的更新之上。
              因此我们不追求铺量，只在意每一部作品是否值得被长期追随，以及读者的每次打开是否满意。
            </p>
          </div>
        </section>

        <section className="mt-12">
          <div className="text-center">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">我们的内容原则</h2>
            <p className="mt-2 text-sm text-foreground-600">四条简单却坚定的准则，贯穿天天漫画网的每一次内容选择。</p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-200/70 bg-background-100 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <div className="text-center">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">读者怎么说</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200/70 bg-background-100 p-6">
                <blockquote className="text-sm leading-relaxed text-foreground-700">「{t.text}」</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <img src={t.avatar} alt={`${t.name} 头像`} className="h-9 w-9 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-12 flex flex-col items-start justify-between gap-5 rounded-lg border border-background-200/70 bg-background-100 p-6 md:flex-row md:items-center md:p-8">
          <div>
            <h2 className="text-lg font-bold text-foreground-950">联系我们</h2>
            <p className="mt-2 text-sm text-foreground-600">有任何建议或合作意向，欢迎随时与我们取得联系。</p>
          </div>
          <Link
            to="/contact"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            联系我们
          </Link>
        </section>
      </div>
    </>
  );
}